import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  MediaAsset, PrintDesign, PrintDesignListItem, PrintDesignRender, PrintOutlineViewKey, PrintTemplate, PrintVehicleTemplate, PrintView,
} from '@wrapforce/shared';
import {
  activeOutlineViews, createPrintDesignSchema, PRINT_OUTLINE_VIEWS, printPanelFileOptionsSchema, printSpecOptionsSchema,
  renderPrintDesignSchema, updatePrintDesignSchema, updatePrintTemplateSchema,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { ApiError } from '@wrapforce/shared/errors';
import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/supabase';

export type CreatePrintDesignInput = z.infer<typeof createPrintDesignSchema>;
export type UpdatePrintDesignInput = z.infer<typeof updatePrintDesignSchema>;
export type UpdatePrintTemplateInput = z.infer<typeof updatePrintTemplateSchema>;
export type RenderPrintDesignInput = z.infer<typeof renderPrintDesignSchema>;
export type PrintSpecOptionsInput = z.infer<typeof printSpecOptionsSchema>;
export type PrintPanelFileOptionsInput = z.infer<typeof printPanelFileOptionsSchema>;

interface PrintDesignListResponse {
  items: PrintDesignListItem[];
  nextCursor: string | null;
}

export function usePrintDesigns(workspaceId: string | null, params?: { vehicleKey?: string; search?: string }) {
  return useQuery({
    queryKey: [workspaceId, 'print-designs', params?.vehicleKey, params?.search],
    queryFn: () => api.get<PrintDesignListResponse>(workspacePath(workspaceId!, '/print-designs'), {
      query: { vehicleKey: params?.vehicleKey, search: params?.search },
    }),
    enabled: !!workspaceId,
  });
}

export function usePrintDesign(workspaceId: string | null, designId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'print-designs', designId],
    queryFn: () => api.get<PrintDesign>(workspacePath(workspaceId!, `/print-designs/${designId}`)),
    enabled: !!workspaceId && !!designId,
    // While the AI-drawn outline is generating, poll for the finished result -
    // same pattern as usePrintDesignRenders below.
    refetchInterval: (query) => (query.state.data?.template.template.outline?.status === 'pending' ? 3000 : false),
  });
}

export function useCreatePrintDesign(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePrintDesignInput) => api.post<PrintDesign>(workspacePath(workspaceId!, '/print-designs'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs'] });
    },
  });
}

export function useUpdatePrintDesign(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePrintDesignInput) => api.patch<PrintDesign>(workspacePath(workspaceId!, `/print-designs/${designId}`), input),
    onSuccess: (design) => {
      queryClient.setQueryData([workspaceId, 'print-designs', designId], design);
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs'] });
    },
  });
}

export function useDeletePrintDesign(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (designId: string) => api.delete<void>(workspacePath(workspaceId!, `/print-designs/${designId}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs'] });
    },
  });
}

export function useSavePrintTemplate(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePrintTemplateInput) =>
      api.put<PrintVehicleTemplate>(workspacePath(workspaceId!, `/print-designs/${designId}/template`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId] });
    },
  });
}

export function useMeasurePrintTemplate(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ template: PrintVehicleTemplate; cached: boolean; creditsBalance: number }>(
      workspacePath(workspaceId!, `/print-designs/${designId}/template/measure`),
      {},
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId] });
    },
  });
}

export function useOutlinePrintTemplate(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts: { redraw?: boolean }) => api.post<{ template: PrintVehicleTemplate; cached: boolean; creditsBalance: number }>(
      workspacePath(workspaceId!, `/print-designs/${designId}/template/outline`),
      opts.redraw ? { redraw: true } : {},
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId] });
    },
  });
}

// Switches to a specific previously generated outline (outlineId matching a
// template.outlineLibrary entry's id) - free and immediate.
export function useSelectPrintOutline(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (outlineId: string) => api.post<{ template: PrintVehicleTemplate }>(
      workspacePath(workspaceId!, `/print-designs/${designId}/template/outline/select`),
      { outlineId },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId] });
    },
  });
}

export function useRenderPrintDesign(workspaceId: string | null, designId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    // `renders` carries the full set (side, three_quarter_front,
    // three_quarter_rear) when Generate produced one; `render` (the side row)
    // is kept for backward compatibility with the single-render, customer-
    // photo-mode response.
    mutationFn: (input: RenderPrintDesignInput) =>
      api.post<{ render: PrintDesignRender; renders?: PrintDesignRender[]; creditsBalance: number }>(
        workspacePath(workspaceId!, `/print-designs/${designId}/renders`), input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId, 'renders'] });
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'print-designs', designId] });
    },
  });
}

export function usePrintDesignRenders(workspaceId: string | null, designId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'print-designs', designId, 'renders'],
    queryFn: () => api.get<PrintDesignRender[]>(workspacePath(workspaceId!, `/print-designs/${designId}/renders`)),
    enabled: !!workspaceId && !!designId,
    refetchInterval: (query) => (query.state.data?.some((r) => r.status === 'pending') ? 3000 : false),
  });
}

export async function downloadPrintSpec(workspaceId: string, designId: string, options: PrintSpecOptionsInput): Promise<void> {
  const accessToken = await getAccessToken();
  const res = await fetch(`${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, `/print-designs/${designId}/spec`)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(options),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new Error(body?.error?.code ?? 'EXPORT_FAILED');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const disposition = res.headers.get('content-disposition');
  const match = disposition?.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `print-wrap-spec-${designId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

// One panel's print-ready PDF (full-resolution artwork, bleed and roll
// tiling built in server-side) - same fetch-and-save pattern as
// downloadPrintSpec. A non-ok response is thrown as an ApiError so the page
// can tell a busy print server (429/RATE_LIMITED) apart from any other
// failure and show a more specific toast.
export async function downloadPanelPrintFile(
  workspaceId: string,
  designId: string,
  view: PrintView,
  panelKey: string,
  options: PrintPanelFileOptionsInput,
): Promise<void> {
  const accessToken = await getAccessToken();
  const res = await fetch(
    `${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, `/print-designs/${designId}/panels/${view}/${panelKey}/print-file`)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(options),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string; requestId?: string } } | null;
    const code = body?.error?.code ?? (res.status === 429 ? 'RATE_LIMITED' : res.status === 404 ? 'NOT_FOUND' : 'INTERNAL');
    throw new ApiError(res.status, {
      error: { code, message: body?.error?.message ?? 'PRINT_FILE_FAILED', requestId: body?.error?.requestId ?? 'unknown' },
    });
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const disposition = res.headers.get('content-disposition');
  const match = disposition?.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `print-wrap-panel-${designId}-${view}-${panelKey}.pdf`;
  a.click();
  // Print files run to hundreds of MB; revoking synchronously can cancel the
  // save in Firefox before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// mediaId identifies one of the design's artwork layers (design.artworks[].id)
// - the artwork library grid fetches each one's bytes separately so it can
// build an object URL per layer.
export async function fetchPrintArtworkBlob(workspaceId: string, designId: string, mediaId: string): Promise<Blob | null> {
  const accessToken = await getAccessToken();
  const res = await fetch(`${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, `/print-designs/${designId}/artwork/${mediaId}`)}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new Error(body?.error?.code ?? 'ARTWORK_FETCH_FAILED');
  }
  return await res.blob();
}

// One render's output image bytes, same-origin (the R2 URL is signed but
// CORS may still block a cross-origin fetch from the browser for the zip
// download) - workspace + design + render scoped on the server.
export async function fetchPrintRenderBlob(workspaceId: string, designId: string, renderId: string): Promise<Blob> {
  const accessToken = await getAccessToken();
  const res = await fetch(`${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, `/print-designs/${designId}/renders/${renderId}/image`)}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new Error(body?.error?.code ?? 'RENDER_IMAGE_FETCH_FAILED');
  }
  return await res.blob();
}

// One layer (image/mask/lines) of one AI-drawn outline view, same-origin
// proxy (R2 has no CORS) - mirrors fetchPrintArtworkBlob's pattern. Omitting
// outlineId fetches the active outline's layer (the original route); passing
// it fetches that specific outlineLibrary entry's layer instead, e.g. for a
// thumbnail of an outline that isn't currently active.
export async function fetchPrintOutlineLayerBlob(
  workspaceId: string,
  designId: string,
  view: PrintOutlineViewKey,
  layer: 'image' | 'mask' | 'lines',
  outlineId?: string,
): Promise<Blob> {
  const accessToken = await getAccessToken();
  const path = outlineId
    ? `/print-designs/${designId}/template/outline/${outlineId}/${view}/${layer}`
    : `/print-designs/${designId}/template/outline/${view}/${layer}`;
  const res = await fetch(
    `${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, path)}`,
    { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new Error(body?.error?.code ?? 'OUTLINE_LAYER_FETCH_FAILED');
  }
  return await res.blob();
}

// Loads every layer of every outline view the editor can currently draw with
// (activeOutlineViews - the active ready outline, or a pending redraw's
// previous views) as object URLs, keyed by media id (so PrintCanvas and the
// rasterizers can look one up by the id already on the template, and a layer
// already fetched for one view is never re-fetched just because another
// view's outline changed). URLs are revoked when replaced or when the hook
// unmounts.
export function useOutlineLayerUrls(
  workspaceId: string | null,
  designId: string | undefined,
  template: PrintTemplate | undefined,
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const urlsRef = useRef<Record<string, string>>({});

  const views = template ? activeOutlineViews(template) : undefined;
  const mediaIdsKey = views
    ? PRINT_OUTLINE_VIEWS.map((view) => {
        const v = views[view];
        return v ? `${v.imageMediaId},${v.maskMediaId},${v.linesMediaId}` : '';
      }).join('|')
    : '';

  useEffect(() => {
    if (!workspaceId || !designId || !views) return;
    const wanted: { view: PrintOutlineViewKey; layer: 'image' | 'mask' | 'lines'; mediaId: string }[] = [];
    for (const view of PRINT_OUTLINE_VIEWS) {
      const v = views[view];
      if (!v) continue;
      wanted.push({ view, layer: 'image', mediaId: v.imageMediaId });
      wanted.push({ view, layer: 'mask', mediaId: v.maskMediaId });
      wanted.push({ view, layer: 'lines', mediaId: v.linesMediaId });
    }
    const missing = wanted.filter((w) => !urlsRef.current[w.mediaId]);
    if (missing.length === 0) return;
    let cancelled = false;
    void Promise.all(missing.map(async (w) => {
      const blob = await fetchPrintOutlineLayerBlob(workspaceId, designId, w.view, w.layer);
      return { mediaId: w.mediaId, url: URL.createObjectURL(blob) };
    })).then((fetched) => {
      if (cancelled) {
        fetched.forEach(({ url }) => URL.revokeObjectURL(url));
        return;
      }
      const next = { ...urlsRef.current };
      fetched.forEach(({ mediaId, url }) => { next[mediaId] = url; });
      urlsRef.current = next;
      setUrls(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, designId, mediaIdsKey]);

  useEffect(() => () => {
    Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  return urls;
}

export async function uploadPrintMedia(
  workspaceId: string,
  kind: 'print_artwork' | 'print_layout' | 'vehicle_photo',
  file: File | Blob,
  designId?: string,
): Promise<MediaAsset> {
  const form = new FormData();
  form.append('kind', kind);
  if (designId) form.append('designId', designId);
  form.append('file', file, file instanceof File ? file.name : 'upload.png');
  return api.upload<MediaAsset>(workspacePath(workspaceId, '/media'), form);
}
